import { ReactNode, useRef } from 'react';
import type { CellContext, ColumnDef, HeaderContext, Row } from '@tanstack/react-table';
import type {
  LevelTableAnyColumn,
  LevelTableColumn,
  LevelTableRenderer,
  LevelTableRow,
} from '../table.types';

type LevelColumnMeta<ColumnDefinitions extends object, TableFunctions extends object> = {
  levelColumn: LevelTableAnyColumn<ColumnDefinitions, TableFunctions>;
  tableFunctions: TableFunctions;
};

export const renderTablePart = <Props extends object>(
  renderer: LevelTableRenderer<Props>,
  props: Props
): ReactNode => (typeof renderer === 'function' ? renderer(props) : renderer);

const LevelTableHeader = <ColumnDefinitions extends object, TableFunctions extends object>(
  context: HeaderContext<ColumnDefinitions, unknown>
): ReactNode => {
  const meta = context.column.columnDef.meta as LevelColumnMeta<ColumnDefinitions, TableFunctions>;
  if (meta.levelColumn.Header === undefined) {
    return null;
  }

  return renderTablePart(meta.levelColumn.Header, {
    ...meta.tableFunctions,
    column: { id: context.column.id },
    data: context.table.options.data,
  });
};

const LevelTableCell = <ColumnDefinitions extends object, TableFunctions extends object>(
  context: CellContext<ColumnDefinitions, unknown>
): ReactNode => {
  const meta = context.column.columnDef.meta as LevelColumnMeta<ColumnDefinitions, TableFunctions>;
  if (meta.levelColumn.Cell === undefined) {
    return renderValue(context.getValue());
  }

  return renderTablePart(meta.levelColumn.Cell, {
    ...meta.tableFunctions,
    value: context.getValue(),
    row: toLevelTableRow(context.row),
    column: { id: context.column.id },
    data: context.table.options.data,
  });
};

export const toLevelTableRow = <ColumnDefinitions extends object>(
  row: Row<ColumnDefinitions>
): LevelTableRow<ColumnDefinitions> => ({
  id: row.id,
  index: row.index,
  original: row.original,
});

export const getLevelTableColumnId = <ColumnDefinitions extends object, TableFunctions>(
  column: LevelTableAnyColumn<ColumnDefinitions, TableFunctions>
): string => column.id ?? column.accessor;

const renderValue = (value: unknown): ReactNode =>
  typeof value === 'string' || typeof value === 'number' ? value : null;

type HeaderRenderer<ColumnDefinitions extends object> = (
  context: HeaderContext<ColumnDefinitions, unknown>
) => ReactNode;

type CellRenderer<ColumnDefinitions extends object> = (
  context: CellContext<ColumnDefinitions, unknown>
) => ReactNode;

type StableColumnRenderers<ColumnDefinitions extends object> = {
  header: HeaderRenderer<ColumnDefinitions>;
  cell: CellRenderer<ColumnDefinitions>;
  renderHeader: HeaderRenderer<ColumnDefinitions>;
  renderCell: CellRenderer<ColumnDefinitions>;
};

type LevelColumnDef<ColumnDefinitions extends object> = {
  id: string;
  accessorFn: (row: ColumnDefinitions) => unknown;
  header: HeaderRenderer<ColumnDefinitions>;
  cell: CellRenderer<ColumnDefinitions>;
};

export const useStableColumnDefs = <
  ColumnDefinitions extends object,
  TableFunctions extends object,
>(
  columns: Parameters<typeof toColumnDefs<ColumnDefinitions, TableFunctions>>[0],
  tableFunctions: TableFunctions
): ColumnDef<ColumnDefinitions>[] => {
  const cacheRef = useRef(new Map<string, StableColumnRenderers<ColumnDefinitions>>());
  const definedColumns = toColumnDefs(columns, tableFunctions);

  return definedColumns.map((definedColumn): ColumnDef<ColumnDefinitions> => {
    const columnId = definedColumn.id ?? '';
    let stable = cacheRef.current.get(columnId);
    if (!stable) {
      stable = {
        renderHeader: () => null,
        renderCell: () => null,
        header: (context) => cacheRef.current.get(columnId)?.renderHeader(context) ?? null,
        cell: (context) => cacheRef.current.get(columnId)?.renderCell(context) ?? null,
      };
      cacheRef.current.set(columnId, stable);
    }

    if (typeof definedColumn.header === 'function') {
      stable.renderHeader = definedColumn.header;
    }
    if (typeof definedColumn.cell === 'function') {
      stable.renderCell = definedColumn.cell;
    }

    return {
      ...definedColumn,
      header: stable.header,
      cell: stable.cell,
    };
  });
};

export const toColumnDefs = <ColumnDefinitions extends object, TableFunctions extends object>(
  columns: readonly (
    | LevelTableColumn<ColumnDefinitions, TableFunctions>
    | LevelTableAnyColumn<ColumnDefinitions, TableFunctions>
  )[],
  tableFunctions: TableFunctions
): LevelColumnDef<ColumnDefinitions>[] =>
  columns.map((levelColumn) => {
    const column: LevelTableAnyColumn<ColumnDefinitions, TableFunctions> = levelColumn;
    const meta: LevelColumnMeta<ColumnDefinitions, TableFunctions> = {
      levelColumn: column,
      tableFunctions,
    };

    return {
      id: getLevelTableColumnId(column),
      accessorFn: (row: ColumnDefinitions) => row[column.accessor],
      meta,
      header: LevelTableHeader,
      cell: LevelTableCell,
    };
  });
